import { type ReactNode, useCallback, useEffect, useState } from 'react';
import { api, type IptvChannel } from '../api.js';
import { CloseIcon, RefreshIcon } from '../Icon.tsx';
import { useHlsPlayer } from '../useHlsPlayer.js';

/**
 * Çoklu ekran (multi-view).
 *
 * Birden çok kanalı aynı anda izlemek (maç, haber, müzik).
 */
const MAX_CELLS = 4;

interface Cell {
  channel: IptvChannel;
  url: string;
}

/** Tek bir hücre: kendi kanal jetonunu alır ve oynatır. */
function MultiViewCell({
  channel,
  onError,
}: {
  channel: IptvChannel;
  onError: (message: string) => void;
}): ReactNode {
  const [url, setUrl] = useState<string | null>(null);
  const player = useHlsPlayer(url);

  useEffect(() => {
    if (!player.error) return;
    onError(player.error);
  }, [player.error, onError]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const grant = await api.iptvPlayback(channel.id);
        if (!cancelled) setUrl(grant.url);
      } catch (cause) {
        if (!cancelled) {
          onError(cause instanceof Error ? cause.message : 'Kanal açılamadı.');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [channel.id, onError]);

  return (
    <div className="live-tv__cell">
      <video
        ref={player.videoRef}
        className="live-tv__cell-video"
        controls
        playsInline
        autoPlay
        muted
        aria-label={channel.name}
      />
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span className="live-tv__cell-name">{channel.name}</span>
        {channel.group !== null && (
          <span style={{ fontSize: 'var(--fs-2xs)', color: 'var(--text-muted)' }}>
            {channel.group}
          </span>
        )}
      </div>
    </div>
  );
}

export function LiveTvMultiView({
  channels,
  onClose,
}: {
  channels: IptvChannel[];
  onClose: () => void;
}): ReactNode {
  const [cells, setCells] = useState<Cell[]>([]);
  const [error, setError] = useState<string | null>(null);

  const onError = useCallback((message: string) => setError(message), []);

  /** Listeden kanalları hücrelere yerleştirir (ilk N kanal). */
  const fill = useCallback(() => {
    setCells(channels.slice(0, MAX_CELLS).map((channel) => ({ channel, url: '' })));
  }, [channels]);

  useEffect(() => {
    fill();
  }, [fill]);

  return (
    <div className="live-tv__multi" role="dialog" aria-modal="true" aria-label="Çoklu Ekran">
      <header className="live-tv__multi-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <h2 className="live-tv__modal-title">Çoklu Ekran (4 Kanal)</h2>
          <span className="live-tv__badge-group">Eşzamanlı Canlı Yayın</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button type="button" className="btn btn--ghost" onClick={fill}>
            <RefreshIcon size={16} />
            <span>Kanalları Yeniden Dağıt</span>
          </button>
          <button type="button" className="btn btn--primary" onClick={onClose}>
            <CloseIcon size={16} />
            <span>Kapat</span>
          </button>
        </div>
      </header>

      {error !== null && <p className="live-tv__error">{error}</p>}

      <div className="live-tv__multi-grid">
        {cells.map((cell) => (
          <MultiViewCell key={cell.channel.id} channel={cell.channel} onError={onError} />
        ))}
      </div>
    </div>
  );
}
